import { useLayoutEffect, useMemo, useRef } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { ReceptionService } from "@/shared/services/types";
import { env } from "@/shared/config/env";

/** Local cache ownership only; never sent as a principal/API parameter. Both
 * reception pages use the same service object and QueryClient. A change of the
 * active principal/service advances the lifetime, including A -> B -> A. */
const services = new WeakMap<ReceptionService, string>();
const active = new WeakMap<QueryClient, ReceptionScope>();
let sequence = 0;
export interface ReceptionScope {
  parts: readonly [string, string, string, number];
  key: string;
  enabled: boolean;
  owns: () => boolean;
}
export interface ReceptionAttempt {
  current: () => boolean;
  run: <T>(operation: () => Promise<T>) => Promise<T>;
}
export function useReceptionScope(reception: ReceptionService, userId: string | null): ReceptionScope {
  const client = useQueryClient();
  let service = services.get(reception);
  if (!service) { service = `reception-service-${++sequence}`; services.set(reception, service); }
  const mode = env.dataMode === "live" ? "member" : "fixture";
  const actor = mode === "fixture" ? "fixture" : userId ?? "";
  const enabled = mode === "fixture" || Boolean(actor.trim());
  const prior = active.get(client);
  if (prior && prior.parts[0] === mode && prior.parts[1] === actor && prior.parts[2] === service) return prior;
  const parts = [mode, actor, service, ++sequence] as const;
  const scope: ReceptionScope = { parts, key: JSON.stringify(parts), enabled, owns: () => active.get(client) === scope };
  active.set(client, scope);
  return scope;
}

/** Every issued operation captures the mount generation. StrictMode cleanup
 * and a real unmount invalidate it even if a later mount uses the same scope.
 * Check errors before the shared Query/MutationCache sees an old auth error. */
export function useReceptionGuard(scope: ReceptionScope) {
  const life = useRef({ mounted: true, generation: 0 });
  useLayoutEffect(() => {
    const frame = life.current; frame.mounted = true;
    return () => { frame.mounted = false; frame.generation++; };
  }, [scope]);
  return useMemo(() => {
    const current = () => life.current.mounted && scope.enabled && scope.owns();
    const attempt = (): ReceptionAttempt => {
      const generation = life.current.generation;
      const valid = () => current() && generation === life.current.generation;
      return { current: valid, run: async <T,>(operation: () => Promise<T>): Promise<T> => {
        if (!valid()) throw new Error("Reception context expired");
        try {
          const result = await operation();
          if (!valid()) throw new Error("Reception context expired");
          return result;
        } catch (error) {
          if (!valid()) throw new Error("Reception context expired");
          throw error;
        }
      } };
    };
    return { current, attempt, run: <T,>(operation: () => Promise<T>) => attempt().run(operation) };
  }, [scope]);
}
